import { Pipe, PipeTransform } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';

interface StatusTexts {
    active: string;
    offline: string;
    deactivated: string;
    day: string;
    days: string;
    hour: string;
    hours: string;
    minute: string;
    minutes: string;
    ago: string;
    justNow: string;
    since: string;
}

interface User {
    lastConnectivityStatusUpdates?: string;
    active?: boolean;
    userConnectivityStatus?: string;
}

@Pipe({
    name: 'ksaTimeAgo',
    pure: false // Mark as impure to update automatically
    ,
    standalone: false
})
export class KsaTimeAgoPipe implements PipeTransform {

    constructor(private _translateService: TranslateService) { }

    transform(user: User): string {
        if (!user || !user.lastConnectivityStatusUpdates) {
            return '';
        }

        const currentTime = new Date();
        const lastConnectivityStatusUpdates = this.convertKsaTimeToDate(user.lastConnectivityStatusUpdates);

        // Calculate time difference in milliseconds
        const timeDifferenceMs = currentTime.getTime() - lastConnectivityStatusUpdates.getTime();
        const minutesSinceUpdate = Math.floor(timeDifferenceMs / (1000 * 60));

        const texts = this.getLocalizedTexts();
        const statusPrefix = this.getStatusPrefix(user, texts);

        return this.formatTimeAgo(minutesSinceUpdate, statusPrefix, texts);
    }

    /**
     * Convert KSA timezone string to a Date object
     * @param dateString - Date string in format "DD/MM/YYYY HH:MM" in KSA timezone
     * @returns Date object properly adjusted for KSA timezone
     */
    private convertKsaTimeToDate(dateString: string): Date {
        // Split the date string into its components
        const parts = dateString.split(/[\s/:]/);

        // Extract date parts
        const day = parseInt(parts[0], 10);
        const month = parseInt(parts[1], 10) - 1; // Month is 0-indexed in JS Date
        const year = parseInt(parts[2], 10);
        const hours = parseInt(parts[3], 10);
        const minutes = parseInt(parts[4], 10);

        // Create a date in UTC, adjusted for KSA timezone (UTC+3)
        const utcDate = new Date(Date.UTC(year, month, day, hours - 3, minutes, 0));

        return utcDate;
    }

    /**
     * Get localized text strings
     */
    private getLocalizedTexts(): StatusTexts {
        const isEnglish = this._translateService.currentLang === 'en';

        return {
            active: isEnglish ? 'Active' : 'نشط',
            offline: isEnglish ? 'Offline' : 'غير نشط',
            deactivated: isEnglish ? 'Deactivated' : 'معطل',
            day: isEnglish ? 'day' : 'يوم',
            days: isEnglish ? 'days' : 'ايام',
            hour: isEnglish ? 'hour' : 'ساعة',
            hours: isEnglish ? 'hours' : 'ساعات',
            minute: isEnglish ? 'minute' : 'دقيقة',
            minutes: isEnglish ? 'minutes' : 'دقائق',
            ago: isEnglish ? 'ago' : '', // "ago" for English
            justNow: isEnglish ? 'just now' : 'الآن',
            since: isEnglish ? '' : 'منذ', // "منذ" for Arabic
        };
    }

    /**
     * Get the appropriate status prefix based on user status
     */
    private getStatusPrefix(user: User, texts: StatusTexts): string {
        if (user.active) {
            return user.userConnectivityStatus === 'ONLINE' ? texts.active : texts.offline;
        }
        return texts.deactivated;
    }

    /**
     * Format the time ago message
     */
    private formatTimeAgo(minutesSinceUpdate: number, statusPrefix: string, texts: StatusTexts): string {
        if (minutesSinceUpdate === 0) {
            return `${statusPrefix} ${texts.justNow}`.trim();
        }

        const timeText = minutesSinceUpdate >= 60
            ? this.formatTimeInHours(minutesSinceUpdate, texts)
            : this.formatTimeInMinutes(minutesSinceUpdate, texts);

        // Use "since" for Arabic and "ago" for English
        const timeTextWithSuffix = this._translateService.currentLang === 'en'
            ? `${statusPrefix} ${timeText} ${texts.ago}`.trim()
            : `${statusPrefix} ${texts.since} ${timeText}`.trim();

        return timeTextWithSuffix;
    }

    private formatTimeInMinutes(minutes: number, texts: StatusTexts): string {
        const minuteLabel = minutes === 1 ? texts.minute : texts.minutes;
        return `${minutes} ${minuteLabel}`;
    }

    private formatTimeInHours(minutes: number, texts: StatusTexts): string {
        const hours = Math.floor(minutes / 60);
        if (hours >= 24) {
            const days = Math.floor(hours / 24);
            const dayLabel = days === 1 ? texts.day : texts.days;
            return `${days} ${dayLabel}`;
        }
        const hourLabel = hours === 1 ? texts.hour : texts.hours;
        return `${hours} ${hourLabel}`;
    }
}
